import { damp } from '@/motion/math';

/**
 * Makes a radial highlight (`light`) follow the mouse over `host` (DESIGN_SYSTEM §4.1) and returns the
 * function that stops it.
 *
 * The highlight moves with `transform` only — nothing is repainted — towards the pointer with frame-rate
 * independent smoothing, and the animation frame loop runs only while it is still travelling. Touch and
 * pen input are ignored: they keep the static highlights drawn into the scene.
 */
export function followPointer(host: HTMLElement, light: HTMLElement): () => void {
  let frame = 0;
  let last = 0;
  let x = host.clientWidth * 0.5;
  let y = host.clientHeight * 0.4;
  let targetX = x;
  let targetY = y;

  const place = (): void => {
    light.style.transform = `translate3d(calc(${x.toFixed(1)}px - 50%), calc(${y.toFixed(1)}px - 50%), 0)`;
  };

  const tick = (time: number): void => {
    const dt = last === 0 ? 16 : Math.min(64, time - last);
    last = time;
    x = damp(x, targetX, 70, dt);
    y = damp(y, targetY, 70, dt);
    place();
    if (Math.abs(targetX - x) > 0.4 || Math.abs(targetY - y) > 0.4) {
      frame = window.requestAnimationFrame(tick);
    } else {
      frame = 0;
      last = 0;
    }
  };

  const onMove = (event: PointerEvent): void => {
    if (event.pointerType !== 'mouse') return;
    const rect = host.getBoundingClientRect();
    targetX = event.clientX - rect.left;
    targetY = event.clientY - rect.top;
    light.dataset['on'] = 'true';
    if (frame === 0) frame = window.requestAnimationFrame(tick);
  };
  const onLeave = (): void => {
    light.dataset['on'] = 'false';
  };

  place();
  host.addEventListener('pointermove', onMove, { passive: true });
  host.addEventListener('pointerleave', onLeave);
  return () => {
    host.removeEventListener('pointermove', onMove);
    host.removeEventListener('pointerleave', onLeave);
    if (frame !== 0) window.cancelAnimationFrame(frame);
    delete light.dataset['on'];
  };
}
